import { useCallback, useRef, useState } from 'react';
import { Upload, X, FileText, Image } from 'lucide-react';
import { clsx } from 'clsx';

interface FileUploadProps {
  accept?: string;
  maxSizeMB?: number;
  onFileSelect: (file: File) => void;
  label?: string;
  preview?: string | null;
}

export function FileUpload({ accept = 'image/*,.pdf', maxSizeMB = 10, onFileSelect, label, preview }: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback((file: File) => {
    if (file.size > maxSizeMB * 1024 * 1024) {
      setError(`El archivo no debe superar ${maxSizeMB}MB`);
      return;
    }
    setError(null);
    setSelectedFile(file);
    onFileSelect(file);
  }, [maxSizeMB, onFileSelect]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const clear = useCallback(() => {
    setSelectedFile(null);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  }, []);

  return (
    <div className="space-y-2">
      {label && <p className="text-sm font-medium text-brand-black">{label}</p>}

      {selectedFile || preview ? (
        <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-xl bg-gray-50">
          {selectedFile?.type.startsWith('image') || preview ? (
            <Image size={24} className="text-brand-yellow flex-shrink-0" />
          ) : (
            <FileText size={24} className="text-brand-yellow flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{selectedFile?.name || 'Archivo cargado'}</p>
            {selectedFile && (
              <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(0)} KB</p>
            )}
          </div>
          <button onClick={clear} className="p-1 hover:bg-gray-200 rounded-lg transition-colors" aria-label="Eliminar archivo">
            <X size={16} />
          </button>
        </div>
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-label={label || 'Subir archivo'}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
          className={clsx(
            'flex flex-col items-center justify-center p-6 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-200',
            dragOver ? 'border-brand-yellow bg-brand-yellow/5' : 'border-gray-300 hover:border-brand-yellow hover:bg-gray-50'
          )}
        >
          <Upload size={28} className="text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">Toca para subir o arrastra un archivo</p>
          <p className="text-xs text-gray-400 mt-1">PDF, JPG, PNG — Máx {maxSizeMB}MB</p>
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
        aria-label={label || 'Subir archivo'}
      />

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
